import { Dispatch } from '../../../clients/quipli-service/order/order';
import React, { Dispatch as ReactDispatch, SetStateAction, useEffect, useState } from 'react';
import { useDispatchApiV1 } from '../../../clients/quipli-service/dispatch/dispatch-api-v1';
import { Configuration } from '../../../clients/quipli-service/configuration/configuration';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { DispatchDirection, ItemDispatchProgress } from '../../../clients/quipli-service/dispatch/dispatch';
import { Spinner } from '../../../components/spinner/spinner';
import { Button } from '../../../components/form/button';
import { SelectForm } from '../../../components/select-form/select-form';

type SelectedProductsRequest = { productId: number; to: ItemDispatchProgress; direction: string }[];
const DispatchTransitionDropdown = ({
  productId,
  direction,
  status,
  request,
  setRequest,
  inTransitEnabled,
}: {
  productId: number;
  direction: DispatchDirection;
  status: ItemDispatchProgress;
  request: SelectedProductsRequest;
  setRequest: ReactDispatch<SetStateAction<SelectedProductsRequest>>;
  inTransitEnabled: boolean;
}) => {
  const [to, setTo] = useState<ItemDispatchProgress>(status);

  const options = [
    { label: 'Pending', value: ItemDispatchProgress.PENDING },
    ...(inTransitEnabled ? [{ label: 'In Transit', value: ItemDispatchProgress.IN_TRANSIT }] : []),
    { label: 'Complete', value: ItemDispatchProgress.COMPLETE },
  ];

  return (
    <SelectForm
      value={options.find((option) => option.value === to)!}
      onChange={(value) => {
        if (value) {
          const newRequest = request.filter((item) => item.productId !== productId);
          newRequest.push({
            productId,
            to: value.value,
            direction: direction == DispatchDirection.DELIVERY ? 'Delivery' : 'Pickup',
          });
          setTo(value.value as ItemDispatchProgress);
          setRequest(newRequest);
        }
      }}
      getOptionLabel={(value) => value.label}
      getOptionValue={(value) => value.value}
      options={options}
      isDisabled={status === ItemDispatchProgress.COMPLETE && direction === DispatchDirection.PICKUP}
    ></SelectForm>
  );
};

export const TransitionDispatchForm = ({
  dispatch,
  direction,
  orderId,
  update,
}: {
  dispatch?: Dispatch;
  direction: DispatchDirection;
  orderId: number;
  update: () => void;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedProductsRequest, setSelectedProductsRequest] = useState<SelectedProductsRequest>([]);
  const [config, setConfig] = useState<Configuration[]>();
  const [_dispatch, setDispatch] = useState<Dispatch | undefined>(dispatch);
  const { transition } = useDispatchApiV1();
  const { getMultipleConfigurations, getDispatch } = useAPI();

  useEffect(() => {
    getMultipleConfigurations(['CONF_DISPATCH_IN_TRANSIT']).then((config) => {
      setConfig(config);
    });
    if (!_dispatch) {
      getDispatch(orderId).then((dispatch) => {
        if (dispatch) {
          setDispatch(dispatch);
        }
      });
    }
  }, []);

  const onSubmit = async () => {
    if (selectedProductsRequest.length && _dispatch) {
      setIsLoading(true);
      try {
        await transition({
          orderId: _dispatch.orderId,
          products: selectedProductsRequest,
        });
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
        update();
      }
    }
  };

  return (
    <div>
      <div className="dialog-actions !px-0 !pb-0">
        <div className={'flex flex-col sm:flex-row actionsContainer'}>
          {isLoading || !_dispatch || !config ? (
            <Spinner />
          ) : (
            <>
              <div className="flex flex-column items-center gap-y-10">
                <table className="table table-striped table-bordered gap-3 w-full">
                  <thead>
                    <tr>
                      <th>Product Name</th>
                    </tr>
                  </thead>
                  <tbody className={'gap-2'}>
                    {direction == DispatchDirection.DELIVERY
                      ? _dispatch.products.delivery
                          ?.filter((_) => _.deliveryStatus !== ItemDispatchProgress.COMPLETE)
                          .map((delivery) => (
                            <tr key={delivery.id}>
                              <td className={'text-left p-2'}>{delivery.name}</td>
                              <td>
                                <DispatchTransitionDropdown
                                  productId={delivery.id}
                                  direction={DispatchDirection.DELIVERY}
                                  status={delivery.deliveryStatus || ItemDispatchProgress.PENDING}
                                  request={selectedProductsRequest}
                                  setRequest={setSelectedProductsRequest}
                                  inTransitEnabled={
                                    config.find((c) => c.variable === 'CONF_DISPATCH_IN_TRANSIT')?.value === '1'
                                  }
                                />
                              </td>
                            </tr>
                          ))
                      : _dispatch.products.pickup
                          ?.filter((p) => p.deliveryStatus === ItemDispatchProgress.COMPLETE || !p.deliveryStatus)
                          .map((pickup) => (
                            <tr key={pickup.id}>
                              <td className={'text-left p-2'}>{pickup.name}</td>
                              <td>
                                <DispatchTransitionDropdown
                                  productId={pickup.id}
                                  direction={DispatchDirection.PICKUP}
                                  status={pickup.pickupStatus || ItemDispatchProgress.PENDING}
                                  request={selectedProductsRequest}
                                  setRequest={setSelectedProductsRequest}
                                  inTransitEnabled={
                                    config.find((c) => c.variable === 'CONF_DISPATCH_IN_TRANSIT')?.value === '1'
                                  }
                                />
                              </td>
                            </tr>
                          ))}
                  </tbody>
                </table>
                <Button className="button button-yellow button-primary m-4" onClick={onSubmit}>
                  Submit
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
